/**
 * Image Storage Utility
 * Manages image data persistence in localStorage with compression and error handling
 */

const STORAGE_KEY = 'virtual_stylist_images';
const MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB limit to be safe (localStorage is typically 5-10MB)

/**
 * Save images to localStorage
 * @param {Array} images - Array of image objects
 * @param {string} context - Context identifier (e.g., 'onboard', 'chat')
 * @returns {boolean} Success status
 */
export const saveImagesToStorage = (images, context = 'default') => {
  try {
    const storageData = getAllStoredImages();
    storageData[context] = images;

    const serialized = JSON.stringify(storageData);
    
    // Check size before saving
    const sizeInBytes = new Blob([serialized]).size;
    if (sizeInBytes > MAX_STORAGE_SIZE) {
      console.warn('Image storage size exceeds limit. Consider clearing old images.');
      // Keep only the most recent images
      storageData[context] = images.slice(-3); // Keep last 3 images
      const reducedSerialized = JSON.stringify(storageData);
      localStorage.setItem(STORAGE_KEY, reducedSerialized);
      return false;
    }

    localStorage.setItem(STORAGE_KEY, serialized);
    console.log(`✅ Saved ${images.length} images to localStorage (${context})`);
    return true;
  } catch (error) {
    console.error('Error saving images to localStorage:', error);
    
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.warn('LocalStorage quota exceeded. Clearing old data...');
      clearImageStorage(context);
    }
    return false;
  }
};

/**
 * Load images from localStorage
 * @param {string} context - Context identifier (e.g., 'onboard', 'chat')
 * @returns {Array} Array of image objects
 */
export const loadImagesFromStorage = (context = 'default') => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const storageData = JSON.parse(stored);
    const images = storageData[context] || [];
    
    console.log(`✅ Loaded ${images.length} images from localStorage (${context})`);
    return images;
  } catch (error) {
    console.error('Error loading images from localStorage:', error);
    return [];
  }
};

/**
 * Get all stored images across all contexts
 * @returns {Object} Object with context keys and image arrays
 */
export const getAllStoredImages = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading localStorage:', error);
    return {};
  }
};

/**
 * Clear images for a specific context
 * @param {string} context - Context identifier
 */
export const clearImageStorage = (context = 'default') => {
  try {
    if (context === 'all') {
      localStorage.removeItem(STORAGE_KEY);
      console.log('🗑️ Cleared all image storage');
    } else {
      const storageData = getAllStoredImages();
      delete storageData[context];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
      console.log(`🗑️ Cleared image storage for context: ${context}`);
    }
  } catch (error) {
    console.error('Error clearing image storage:', error);
  }
};

/**
 * Get storage usage info
 * @returns {Object} Storage size and usage information
 */
export const getStorageInfo = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const sizeInBytes = stored ? new Blob([stored]).size : 0;
    const sizeInKB = Math.round(sizeInBytes / 1024);
    const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
    const percentUsed = ((sizeInBytes / MAX_STORAGE_SIZE) * 100).toFixed(1);

    return {
      sizeInBytes,
      sizeInKB,
      sizeInMB,
      percentUsed: parseFloat(percentUsed),
      maxSizeMB: (MAX_STORAGE_SIZE / (1024 * 1024)).toFixed(1)
    };
  } catch (error) {
    console.error('Error getting storage info:', error);
    return null;
  }
};

/**
 * Remove a specific image by ID from storage
 * @param {number} imageId - Image ID to remove
 * @param {string} context - Context identifier
 */
export const removeImageFromStorage = (imageId, context = 'default') => {
  try {
    const storageData = getAllStoredImages();
    if (storageData[context]) {
      storageData[context] = storageData[context].filter(img => img.id !== imageId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
      console.log(`🗑️ Removed image ${imageId} from storage (${context})`);
    }
  } catch (error) {
    console.error('Error removing image from storage:', error);
  }
};

/**
 * Check if localStorage is available
 * @returns {boolean}
 */
export const isStorageAvailable = () => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};
